/**
 * National Collegiate Rugby (NCR) partnership. OptimX Sports is NCR's official website partner,
 * and NCR teams get their first year free. Nothing NCR-related is saved to the database: the
 * signup answer and the free-year claim only travel in the emails sent to the OptimX inbox.
 */
import { SportsType } from './enums/SportsType';
import { getWebsiteURL } from './Others';
import { isValidString } from './Validity';

export const NCR_NAME = 'National Collegiate Rugby';
export const NCR_URL = 'https://ncr.rugby/';
export const NCR_OFFER = 'NCR teams get their first year of OptimX Sports free.';
export const NCR_PORTAL_LINK = '/portal/ncr';

/* eslint-disable no-unused-vars */
export enum NCRMembership {
    NONE = '',
    NCR = 'ncr',
    OTHER = 'other',
}
/* eslint-enable no-unused-vars */

export const NCR_MEMBERSHIP_OPTIONS = [
    { value: NCRMembership.NONE, title: 'Not Selected' },
    { value: NCRMembership.NCR, title: 'NCR team' },
    { value: NCRMembership.OTHER, title: 'Others team' },
];

export const getNCRMembershipName = (membership: NCRMembership): string => {
    if (membership === NCRMembership.NCR) {
        return 'NCR team';
    } if (membership === NCRMembership.OTHER) {
        return 'Others team';
    }
    return 'Not asked';
};

// Sentence added to the signup emails. Only rugby signups are asked, so everyone else's email is unchanged.
export const getNCRMembershipMailText = (ncrMembership: NCRMembership): string => {
    if (ncrMembership === NCRMembership.NONE) {
        return '';
    }
    return ` NCR or Others Team: ${getNCRMembershipName(ncrMembership)}${ncrMembership === NCRMembership.NCR ? ' (NCR teams get their first year free)' : ''}.`;
};

export const isRugbySportsType = (sportsType: SportsType): boolean => {
    return sportsType === SportsType.WOMENSRUGBY || sportsType === SportsType.MENSRUGBY;
};

// A team has a domain once its site URL is no longer the shared test-site link.
export const hasPublishedDomain = (globalState: any): boolean => {
    const url = getWebsiteURL(globalState);
    return isValidString(url) && !url.includes('?team=');
};

/**
 * Only rugby teams that have neither a domain nor an active (or trialing) website/domain
 * subscription can still claim the NCR free year.
 */
export const canClaimNCRFreeYear = (globalState: any, websiteSubscriptionActive: boolean, domainSubscriptionActive: boolean): boolean => {
    return isRugbySportsType(globalState.sportsType)
        && !hasPublishedDomain(globalState)
        && !websiteSubscriptionActive
        && !domainSubscriptionActive;
};

// NCR member teams, as provided by NCR (alphabetical).
export const NCR_TEAMS: string[] = [
    'Adrian College Men',
    'Adrian College Women',
    'Alfred University Men',
    'Alfred University Women',
    'American International College Men',
    'American University Men',
    'American University Women',
    'Angelo State University Men',
    'Appalachian State University Men',
    'Appalachian State University Women',
    'Aquinas College Men',
    'Aquinas College Women',
    'Arizona State University Women',
    'Auburn University Men',
    'Augustana University Women',
    'Babson College Men',
    'Babson College Women',
    'Baldwin Wallace University Women',
    'Ball State University Women',
    'Bard College Men',
    'Bates College Men',
    'Baylor University Men',
    'Baylor University Women',
    'Belmont Abbey College Men',
    'Bemidji State University Men',
    'Bemidji State University Women',
    'Benedictine College Men',
    'Benedictine College Women',
    'Benedictine University Men',
    'Bentley University Men',
    'Bentley University Women',
    'Bloomsburg University Men',
    'Bloomsburg University Women',
    'Boise State University Men',
    'Boise State University Women',
    'Boston College Men',
    'Boston College Women',
    'Boston University Men',
    'Boston University Women',
    'Bowdoin College Men',
    'Bowling Green State University Men',
    'Bowling Green State University Women',
    'Brandeis University Women',
    'Bridgewater State University Men',
    'Brown University Men',
    'Bryant University Men',
    'Bryant University Women',
    'Bucknell University Men',
    'Calvin University Men',
    'Calvin University Women',
    'Carleton College Men',
    'Carleton College Women',
    'Catholic University Men',
    'Catholic University Women',
    'Cedarville University Men',
    'Central Connecticut State University Men',
    'Central Connecticut State University Women',
    'Central Michigan University Men',
    'Central Michigan University Women',
    'Central Oregon Community College Men',
    'Christendom College Men',
    'Christopher Newport University Men',
    'Christopher Newport University Women',
    'Clarkson University Men',
    'Clarkson University Women',
    'Clemson University Men',
    'Clemson University Women',
    'Coastal Carolina University Men',
    'Coastal Carolina University Women',
    'Colby College Men',
    'Colby College Women',
    'Colgate University Men',
    'Colgate University Women',
    'College of Charleston Men',
    'College of Charleston Women',
    'College of St. Benedict Women',
    'College of St. Scholastica Men',
    'College of the Holy Cross Men',
    'College of William & Mary Men',
    'College of William & Mary Women',
    'Colorado College Men',
    'Colorado College Women',
    'Colorado Mesa University Men',
    'Colorado Mesa University Women',
    'Colorado School of Mines Men',
    'Colorado School of Mines Women',
    'Colorado State University Women',
    'Columbia University Men',
    'Columbia University Women',
    'Connecticut College Women',
    'Cornell University Men',
    'Cornell University Women',
    'Curry College Men',
    'Dartmouth College Men',
    'Denison University Men',
    'Denison University Women',
    'DePaul University Men',
    'DePaul University Women',
    'DeSales University Men',
    'DeSales University Women',
    'Drexel University Men',
    'Drexel University Women',
    'Drury University Men',
    'Drury University Women',
    'Duke University Men',
    'East Carolina University Men',
    'East Stroudsburg University Men',
    'East Stroudsburg University Women',
    'Eastern Connecticut State University Men',
    'Eastern Connecticut State University Women',
    'Eastern Kentucky University Men',
    'Eckerd College Women',
    'Elon University Men',
    'Elon University Women',
    'Emory and Henry College Men',
    'Endicott College Men',
    'Endicott College Women',
    'Fairfield University Men',
    'Fairfield University Women',
    'Ferris State University Men',
    'Ferris State University Women',
    'Florida International University Women',
    'Florida State University Women',
    'Fordham University Men',
    'Fordham University Women',
    'Franciscan University Men',
    'Franklin & Marshall College Men',
    'Franklin and Marshall College Women',
    'Frostburg State University Men',
    'Furman University Men',
    'Gannon University Women',
    'Geneva College Men',
    'Geneva College Women',
    'George Mason University Men',
    'George Mason University Women',
    'George Washington University Men',
    'George Washington University Women',
    'Georgetown University Men',
    'Georgetown University Women',
    'Georgia College and State University Men',
    'Georgia College and State University Women',
    'Georgia Institute of Technology Men',
    'Georgia Institute of Technology Women',
    'Gettysburg College Men',
    'Gonzaga University Men',
    'Grace College Women',
    'Grand Valley State University Men',
    'Grand Valley State University Women',
    'Grove City College Men',
    'Gustavus Adolphus College Women',
    'Hamilton College Men',
    'Hamilton College Women',
    'Hampden-Sydney College Men',
    'Harvard University Men',
    'Haverford College Men',
    'Hillsdale College Men',
    'Hofstra University Men',
    'Hofstra University Women',
    'Howard University Women',
    'Idaho State University Men',
    'Idaho State University Women',
    'Illinois State University Men',
    'Illinois State University Women',
    'Indiana Institute of Technology Men',
    'Indiana University Men',
    'Indiana University of Pennsylvania Men',
    'Indiana University of Pennsylvania Women',
    'Indiana University Women',
    'Iona University Men',
    'Iowa Central Community College Men',
    'Iowa State University Women',
    'Ithaca College Men',
    'Ithaca College Women',
    'James Madison University Men',
    'James Madison University Women',
    'John Brown University Men',
    'John Brown University Women',
    'John Carroll University Men',
    'John Carroll University Women',
    'Johns Hopkins University Men',
    'Juniata College Men',
    'Kansas State University Men',
    'Keene State College Men',
    'Keene State College Women',
    'Kennesaw State University Men',
    'Kennesaw State University Women',
    'Kent State University Men',
    'Kent State University Women',
    'Kenyon College Men',
    'Kutztown University Men',
    'Kutztown University Women',
    'Lafayette College Men',
    'Lafayette College Women',
    'Lander University Men',
    'Lander University Women',
    'Lasell University Women',
    'Le Moyne College Men',
    'Le Moyne College Women',
    'Lee University Women',
    'Lehigh University Men',
    'Lehigh University Women',
    'LeTourneau University Men',
    'LeTourneau University Women',
    'Longwood University Women',
    'Louisiana State University Men',
    'Louisiana State University Women',
    'Louisiana Tech University Men',
    'Loyola University - Chicago Men',
    'Loyola University - Maryland Men',
    'Loyola University - Maryland Women',
    'Loyola University Chicago Women',
    'Macalester College Men',
    'Macalester University Women',
    'Marian University Men',
    'Marist University Men',
    'Marist University Women',
    'Marquette University Men',
    'Marquette University Women',
    'Maryville University Men',
    'Massachusetts Maritime Academy Men',
    'Merrimack College Men',
    'Merrimack College Women',
    'Miami University of Ohio Men',
    'Michigan State University Men',
    'Michigan State University Women',
    'Michigan Technological University Men',
    'Michigan Technological University Women',
    'Middle Tennessee State University Men',
    'Middle Tennessee State University Women',
    'Middlebury College Men',
    'Middlebury College Women',
    'Millersville University of Pennsylvania Men',
    'Millersville University Women',
    'Milwaukee School of Engineering Men',
    'Minnesota State University - Mankato Women',
    'Minnesota State University - Moorhead Men',
    'Minnesota State University Moorhead- Women',
    'Mississippi State University Men',
    'Montana State University Men',
    'Montana State University Women',
    'Montclair State University Men',
    'Montclair State University Women',
    'Mount Holyoke College Women',
    'Nazareth University Men',
    'Nazareth University Women',
    'New England College Women',
    'New Mexico Institute of Mining and Technology Men',
    'New Mexico Institute of Mining and Technology Women',
    'New Mexico State University Women',
    'New Saint Andrews College Men',
    'Niagara University Men',
    'Niagara University Women',
    'Nichols College Men',
    'Nichols College Women',
    'North Carolina State University Men',
    'North Carolina State University Women',
    'North Dakota State University Men',
    'North Dakota State University Women',
    'Northeastern University Men',
    'Northeastern University Women',
    'Northern Illinois University Men',
    'Northern Illinois University Women',
    'Northern Michigan University Men',
    'Northern Michigan University Women',
    'Northwestern University Men',
    'Northwestern University Women',
    'Norwich University Men',
    'Norwich University Women',
    'Oakland University Women',
    'Ohio University Men',
    'Ohio University Women',
    'Ohio Wesleyan University Women',
    'Oklahoma State University Men',
    'Oklahoma University Men',
    'Oklahoma University Women',
    'Ole Miss: University of Mississippi Men',
    'Oregon Institute of Technology Men',
    "Paul Smith's College Men",
    'Penn West University - California Women',
    'Pennsylvania State University Women',
    'Pennsylvania Western University Clarion Women',
    'Pittsburg State University Men',
    'Pittsburg State University Women',
    'Plymouth State University Men',
    'Plymouth State University Women',
    'Princeton University Men',
    'Providence College Men',
    'Providence College Women',
    'Purdue University Men',
    'Purdue University Women',
    'Queens University Men',
    'Quinnipiac University Men',
    'Radford University Men',
    'Reed College Women',
    'Rensselaer Polytechnic Institute Men',
    'Rensselaer Polytechnic Institute Women',
    'Rice University Men',
    'Rice University Women',
    'Roanoke College Men',
    'Roanoke College Women',
    'Robert Morris University Men',
    'Robert Morris University Women',
    'Rochester Institute of Technology Men',
    'Rochester Institute of Technology Women',
    'Roger Williams University Men',
    'Roger Williams University Women',
    'Rowan University Men',
    'Rowan University Women',
    'Rutgers University Men',
    'Rutgers University Women',
    'Sacred Heart University Men',
    'Saginaw Valley State University Men',
    'Saginaw Valley State University Women',
    'Saint Anselm College Men',
    'Saint Lawrence University Women',
    'Saint Louis University Men',
    'Saint Louis University Women',
    'Saint Olaf College Women',
    'Salisbury University Men',
    'Salisbury University Women',
    'Salve Regina University Men',
    'Salve Regina University Women',
    'Sam Houston State University Men',
    'Sam Houston State University Women',
    'Seton Hall University Men',
    'Sewanee: The University of the South Men',
    'Shippensburg University Women',
    'Siena College Men',
    'Siena College Women',
    'Slippery Rock University Men',
    'Smith College Women',
    'South Dakota State University Men',
    'South Dakota State University Women',
    'Southern Connecticut State University Men',
    'Southern Connecticut State University Women',
    'Southern Illinois University Men',
    'Southern Illinois University Women',
    'Southern Methodist University Men',
    'Southern Nazarene University Men',
    'Southern Nazarene University Women',
    'Southern Utah University Women',
    'Southwest Minnesota State University Men',
    'Southwest Minnesota State University Women',
    'Springfield College Men',
    'Springfield College Women',
    'St. Bonaventure University Men',
    'St. Bonaventure University Women',
    'St. Edwards University Men',
    "St. John's University Men",
    "St. Joseph's University Men",
    "St. Joseph's University Women",
    'St. Lawrence University Men',
    "St. Mary's College Maryland Men",
    "St. Mary's College Maryland Women",
    "St. Michael's College Men",
    "St. Michael's College Women",
    'Stony Brook University Men',
    'Stony Brook University Women',
    'SUNY - Binghamton Men',
    'SUNY - Binghamton Women',
    'SUNY - Brockport Men',
    'SUNY - Brockport Women',
    'SUNY - Buffalo State College Men',
    'SUNY - Buffalo State College Women',
    'SUNY - Cortland Women',
    'SUNY - Fredonia Men',
    'SUNY - Fredonia Women',
    'SUNY - Geneseo Men',
    'SUNY - Geneseo Women',
    'SUNY - Maritime College Men',
    'SUNY - New Paltz Men',
    'SUNY - New Paltz Women',
    'SUNY - Oneonta Women',
    'SUNY - Oswego Men',
    'SUNY - Oswego Women',
    'SUNY - Plattsburgh Men',
    'SUNY - Plattsburgh Women',
    'SUNY - Potsdam Men',
    'SUNY - Potsdam Women',
    'Susquehanna University Men',
    'Susquehanna University Women',
    'Swarthmore College Men',
    'Syracuse University Men',
    'Syracuse University Women',
    'Tarleton State University Men',
    'Taylor University Men',
    'Taylor University Women',
    'Temple University Men',
    'Temple University Women',
    'Tennessee Tech University Men',
    'Texas A&M University - Corpus Christi Men',
    'Texas A&M University - Corpus Christi Women',
    'Texas A&M University Men',
    'Texas A&M University Women',
    'Texas Christian University Men',
    'Texas State University Men',
    'Texas State University Women',
    'Texas Tech University Men',
    'Texas Tech University Women',
    'The Citadel Men',
    'The Citadel Women',
    'The College of New Jersey Men',
    'The Ohio State University Women',
    'Thomas College Men',
    'Thomas College Women',
    'Thomas More University Men',
    'Towson University Men',
    'Towson University Women',
    'Trinity College Men',
    'Trinity College Women',
    'Trinity University San Antonio Men',
    'Tufts University Men',
    'Tufts University Women',
    'Tulane University Men',
    'Union College Men',
    'Union College Women',
    'United States Coast Guard Academy Men',
    'United States Coast Guard Academy Women',
    'United States Merchant Marine Academy',
    'University at Albany Men',
    'University at Buffalo Men',
    'University at Buffalo Women',
    'University of Alabama Men',
    'University of Alabama Women',
    'University of Arkansas Men',
    'University of Arkansas Women',
    'University of California, Los Angeles Men',
    'University of Central Florida Women',
    'University of Chicago Men',
    'University of Chicago Women',
    'University of Cincinnati Men',
    'University of Cincinnati Women',
    'University of Colorado at Colorado Springs Women',
    'University of Colorado Women',
    'University of Connecticut Men',
    'University of Connecticut Women',
    'University of Dallas Men',
    'University of Dayton Men',
    'University of Delaware Men',
    'University of Delaware Women',
    'University of Denver Men',
    'University of Denver Women',
    'University of Florida Women',
    'University of Georgia Men',
    'University of Georgia Women',
    'University of Hartford Men',
    'University of Hartford Women',
    'University of Houston Men',
    'University of Houston Women',
    'University of Idaho Men',
    'University of Illinois - Chicago Men',
    'University of Illinois - Chicago Women',
    'University of Illinois Women',
    'University of Iowa Women',
    'University of Kansas Men',
    'University of Kansas Women',
    'University of Kentucky Men',
    'University of Louisville Men',
    'University of Maine - Farmington Men',
    'University of Maine - Farmington Women',
    'University of Maine - Orono Men',
    'University of Maine - Orono Women',
    'University of Mary Washington Women',
    'University of Maryland - Baltimore County Men',
    'University of Maryland - Baltimore County Women',
    'University of Maryland Men',
    'University of Maryland Women',
    'University of Massachusetts - Amherst Men',
    'University of Massachusetts - Amherst Women',
    'University of Massachusetts - Dartmouth Men',
    'University of Massachusetts - Lowell Men',
    'University of Massachusetts - Lowell Women',
    'University of Memphis Men',
    'University of Michigan Men',
    'University of Michigan Women',
    'University of Minnesota - Duluth Men',
    'University of Minnesota - Duluth Women',
    'University of Minnesota - Mankato Men',
    'University of Minnesota Women',
    'University of Missouri - Science & Technology Men',
    'University of Missouri - Science & Technology Women',
    'University of Missouri Men',
    'University of Missouri Women',
    'University of Montana Men',
    'University of Nebraska - Lincoln Women',
    'University of Nebraska Men',
    'University of New England Men',
    'University of New Hampshire Men',
    'University of New Hampshire Women',
    'University of New Haven Men',
    'University of New Haven Women',
    'University of New Haven Women (Varsity)',
    'University of North Alabama Men',
    'University of North Carolina - Chapel Hill Men',
    'University of North Carolina - Chapel Hill Women',
    'University of North Carolina - Charlotte Men',
    'University of North Carolina - Charlotte Women',
    'University of North Carolina - Greensboro Women',
    'University of North Carolina - Wilmington Men',
    'University of North Carolina - Wilmington Women',
    'University of North Carolina Greensboro Men',
    'University of North Dakota Men',
    'University of North Dakota Women',
    'University of North Georgia Men',
    'University of North Georgia Women',
    'University of North Texas Men',
    'University of North Texas Women',
    'University of Northern Colorado Women',
    'University of Northern Iowa Men',
    'University of Northern Iowa Women',
    'University of Notre Dame Men',
    'University of Notre Dame Women',
    'University of Pennsylvania Men',
    'University of Pennsylvania Women',
    'University of Pittsburgh - Johnstown Men',
    'University of Pittsburgh - Johnstown Women',
    'University of Pittsburgh Men',
    'University of Pittsburgh Women',
    'University of Puget Sound Women',
    'University of Rhode Island Men',
    'University of Rhode Island Women',
    'University of Richmond Men',
    'University of Rio Grande Men',
    'University of Rochester Men',
    'University of Rochester Women',
    'University of Scranton Men',
    'University of Scranton Women',
    'University of South Carolina Men',
    'University of South Carolina Women',
    'University of South Dakota Women',
    'University of South Florida Women',
    'University of St. Thomas Men',
    'University of St. Thomas Women',
    'University of Tennessee Men',
    'University of Tennessee Women',
    'University of Texas - Austin Men',
    'University of Texas - Austin Women',
    'University of Texas - Dallas Men',
    'University of Texas - San Antonio Men',
    'University of Texas - San Antonio Women',
    'University of Utah Women',
    'University of Vermont Men',
    'University of Vermont Women',
    'University of Virginia Men',
    'University of Virginia Women',
    'University of Wisconsin - Eau Claire Men',
    'University of Wisconsin - Eau Claire Women',
    'University of Wisconsin - La Crosse Men',
    'University of Wisconsin - LaCrosse Women',
    'University of Wisconsin - Milwaukee Men',
    'University of Wisconsin - Milwaukee Women',
    'University of Wisconsin - Oshkosh Women',
    'University of Wisconsin - Platteville Men',
    'University of Wisconsin - Platteville Women',
    'University of Wisconsin - Stevens Point Men',
    'University of Wisconsin - Stevens Point Women',
    'University of Wisconsin - Stout Polytechnic Men',
    'University of Wisconsin - Stout Polytechnic Women',
    'University of Wisconsin - Whitewater Men',
    'University of Wisconsin - Whitewater Women',
    'University of Wisconsin Men',
    'University of Wisconsin Women',
    'University of Wyoming Women',
    'Utah State University Women',
    'Utah Tech University Women',
    'Valley Forge Military College Men',
    'Vanderbilt University Men',
    'Vanderbilt University Women',
    'Vassar College Men',
    'Vassar College Women',
    'Vermont State University Castleton Women',
    'Villanova University Men',
    'Virginia Commonwealth University Men',
    'Virginia Commonwealth University Women',
    'Virginia Military Institute Men',
    'Virginia Polytechnic Institute Men',
    'Virginia Polytechnic Institute Women',
    'Wabash College Men',
    'Wake Forest University Men',
    'Walsh University Men',
    'Walsh University Women',
    'Washington and Lee University Men',
    'Washington and Lee University Women',
    'Washington University - St. Louis Men',
    'Wayne State College Women',
    'Weber State University Men',
    'Weber State University Women',
    'Wellesley College Women',
    'Wentworth Institute of Technology - Women',
    'Wentworth Institute of Technology Men',
    'Wesleyan University Men',
    'Wesleyan University Women',
    'West Chester University Men',
    'West Virginia University Men',
    'West Virginia University Women',
    'Western Carolina University Men',
    'Western Carolina University Women',
    'Western Kentucky University Men',
    'Western Michigan University Men',
    'Western Michigan University Women',
    'Western New England University Men',
    'Western Oregon University Men',
    'Western Oregon University Women',
    'Westfield State University Women',
    'Wheaton College Men',
    'Wheaton College Women',
    'Wheeling University Men',
    'Widener University Men',
    'Willamette University Women',
    'Williams College Men',
    'Williams College Women',
    'Wingate University Men',
    'Winona State University Men',
    'Winona State University Women',
    'Worcester Polytechnic Institute Men',
    'Worcester Polytechnic Institute Women',
    'Xavier University Men',
    'Yale University Men',
    'Yale University Women',
    'York College Men',
    'York College Women',
];
